import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import WingitPublicPage from './components/WingitPublicPage';
import WingitPanel from './components/WingitPanel';
import WingitButton from './components/WingitButton';
import WingitInput from './components/WingitInput';
import WingitField from './components/WingitField';
import wingitDesign from './designSystem';

export default function LoginPage() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
const [resetMessage, setResetMessage] = useState('');
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error: loginError, data } = await supabase.auth.signInWithPassword({
        email: form.email.trim(),
        password: form.password,
      });

      if (loginError) {
        setError(loginError.message);
        return;
      }

      const user = data?.user;

      if (!user) {
        setError('Could not get user info.');
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .or(`id.eq.${user.id},email.eq.${user.email}`)
        .maybeSingle();

      if (profileError) {
        setError(profileError.message);
        return;
      }

      if (profile) {
        const { error: updateError } = await supabase
          .from('users')
          .update({
            id: user.id,
            auth_id: user.id,
            email: user.email,
          })
          .eq('email', user.email);

        if (updateError) {
          setError(updateError.message);
          return;
        }
      } else {
        const { error: insertError } = await supabase.from('users').insert([
          {
            id: user.id,
            auth_id: user.id,
            email: user.email,
            full_name: user.user_metadata?.full_name || '',
            phone: user.user_metadata?.phone || '',
            role: user.user_metadata?.role || '',
            licence_type: user.user_metadata?.licence_type || '',
            home_airfield: user.user_metadata?.home_airfield || '',
            membership_tier: user.user_metadata?.membership_tier || 'calendar',
            membership_billing_cycle: user.user_metadata?.membership_billing_cycle || 'monthly',
            organisation_id: user.user_metadata?.organisation_id || null,
            organisation_code: user.user_metadata?.organisation_code || '',
            org_name: user.user_metadata?.org_name || '',
          },
        ]);

        if (insertError) {
          setError(insertError.message);
          return;
        }
      }

      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  }
async function handleResetPassword() {
  const email = form.email.trim();

  setError('');
  setResetMessage('');

  if (!email) {
    setError('Enter your email address first.');
    return;
  }

  setResetLoading(true);

  try {
    const { error: resetError } =
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/`,
      });

    if (resetError) {
      setError(resetError.message);
      return;
    }

    setResetMessage(
      'Reset request accepted. If an account exists for this email and email delivery is configured, instructions should arrive shortly. Check spam, or ask an administrator to verify email delivery settings.'
    );
  } catch (err) {
    setError(err.message || 'Could not send the password reset email.');
  } finally {
    setResetLoading(false);
  }
}
  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  return (
    <WingitPublicPage
      rightActionLabel="Register"
      onRightAction={() => navigate('/register')}
    >
      <main style={styles.hero}>
        <h1 style={styles.title}>Welcome back.</h1>

        <p style={styles.subtitle}>
          Log in to access your aircraft, bookings and organisation tools.
        </p>

        <WingitPanel>
          <form onSubmit={handleSubmit} autoComplete="off" style={styles.form}>
            <WingitField label="Email address">
              <WingitInput
                name="email"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </WingitField>

            <WingitField label="Password">
              <div style={styles.passwordWrap}>
                <WingitInput
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Your password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  style={{ paddingRight: 46 }}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  style={styles.eyeButton}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
            </WingitField>
<button
  type="button"
  onClick={handleResetPassword}
  disabled={resetLoading}
  style={{
    ...styles.footerLink,
    justifySelf: 'end',
    opacity: resetLoading ? 0.6 : 1,
  }}
>
  {resetLoading ? 'Sending reset email...' : 'Forgot password?'}
</button>

{resetMessage && (
  <div
    role="status"
    style={{
      color: '#b7f7cf',
      background: 'rgba(44, 190, 110, 0.08)',
      border: '1px solid rgba(44, 190, 110, 0.22)',
      borderRadius: 10,
      padding: '0.75rem',
    }}
  >
    {resetMessage}
  </div>
)}
            {error && <div style={styles.error}>{error}</div>}

            <WingitButton type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Log in'}
            </WingitButton>
          </form>
        </WingitPanel>

        <div style={styles.footer}>
          New to Wingit?{' '}
          <button type="button" onClick={() => navigate('/register')} style={styles.footerLink}>
            Create or join an organisation
          </button>
        </div>
      </main>
    </WingitPublicPage>
  );
}

const styles = {
  hero: {
    ...wingitDesign.publicPages.hero,
    maxWidth: 560,
  },

  title: wingitDesign.publicPages.title,

  subtitle: {
    ...wingitDesign.publicPages.subtitle,
    maxWidth: 520,
  },

  form: {
    display: 'grid',
    gap: '0.9rem',
  },

  passwordWrap: {
    position: 'relative',
  },

  eyeButton: {
    position: 'absolute',
    top: '50%',
    right: 8,
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    padding: 4,
    margin: 0,
    lineHeight: 0,
    cursor: 'pointer',
    color: '#8fa8c5',
  },

  error: {
    color: '#ffb3b3',
    background: 'rgba(255,77,77,0.08)',
    border: '1px solid rgba(255,77,77,0.18)',
    borderRadius: 10,
    padding: '0.75rem',
  },

  footer: {
    marginTop: '1.2rem',
    color: '#8fa8c5',
    fontSize: '0.95rem',
  },

  footerLink: {
    border: 'none',
    background: 'transparent',
    color: '#55c7ff',
    fontWeight: 800,
    cursor: 'pointer',
    padding: 0,
  },
};